(() => {
  if (customElements.get("hero-slideshow")) return;
  class HeroSlideshow extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.slides = Array.from(this.querySelector(".native-hero-container").children);
      this.buttons = Array.from(this.querySelectorAll("[data-hero-index]"));
      this.pauseButton = this.querySelector("[data-hero-pause]");
      this.status = this.querySelector("[data-hero-status]");
      this.index = 0;
      this.requestId = 0;
      this.userPaused = false;
      this.hovered = false;
      this.focused = false;
      this.ready = false;
      this.delay = Math.max(3000, Number(this.dataset.autoplayTimeout) || 5000);
      this.buttons.forEach((button) => button.addEventListener("click", () => {
        this.show(Number(button.dataset.heroIndex), true);
      }, options));
      this.pauseButton?.addEventListener("click", () => {
        this.userPaused = !this.userPaused;
        this.toggleAttribute("data-user-paused", this.userPaused);
        this.pauseButton.setAttribute("aria-label", this.userPaused ? this.pauseButton.dataset.playLabel : this.pauseButton.dataset.pauseLabel);
        this.schedule();
      }, options);
      this.addEventListener("pointerenter", (event) => {
        if (event.pointerType === "mouse") {
          this.hovered = true;
          this.schedule();
        }
      }, options);
      this.addEventListener("pointerleave", () => {
        this.hovered = false;
        this.schedule();
      }, options);
      this.addEventListener("focusin", () => {
        this.focused = true;
        this.schedule();
      }, options);
      this.addEventListener("focusout", (event) => {
        this.focused = this.contains(event.relatedTarget);
        this.schedule();
      }, options);
      this.addEventListener("keydown", (event) => {
        if (!event.target.closest("[data-hero-index]")) return;
        let next;
        if (event.key === "ArrowRight") next = (this.index + 1) % this.slides.length;
        if (event.key === "ArrowLeft") next = (this.index - 1 + this.slides.length) % this.slides.length;
        if (event.key === "Home") next = 0;
        if (event.key === "End") next = this.slides.length - 1;
        if (next === undefined) return;
        event.preventDefault();
        this.buttons[next]?.focus();
        this.show(next, true);
      }, options);
      document.addEventListener("visibilitychange", () => this.schedule(), options);
      const firstImage = this.slides[0]?.querySelector("img");
      const begin = () => {
        this.ready = true;
        this.schedule();
      };
      if (firstImage && !firstImage.complete) {
        firstImage.addEventListener("load", begin, { ...options, once: true });
        firstImage.addEventListener("error", begin, { ...options, once: true });
      } else begin();
    }
    disconnectedCallback() {
      clearTimeout(this.timer);
      this.requestId++;
      this.controller?.abort();
      this.controller = null;
    }
    schedule() {
      clearTimeout(this.timer);
      if (!this.ready || this.dataset.autoplay !== "true" || this.slides.length < 2 || this.userPaused || this.hovered || this.focused || document.hidden) return;
      this.timer = setTimeout(() => this.show((this.index + 1) % this.slides.length, false), this.delay);
    }
    async show(index, manual) {
      if (!this.slides[index] || index === this.index) {
        this.schedule();
        return;
      }
      clearTimeout(this.timer);
      const requestId = ++this.requestId;
      const image = this.slides[index].querySelector("img");
      if (image) {
        image.loading = "eager";
        if (!image.complete) {
          await new Promise((resolve) => {
            image.addEventListener("load", resolve, { once: true, signal: this.controller.signal });
            image.addEventListener("error", resolve, { once: true, signal: this.controller.signal });
            this.controller.signal.addEventListener("abort", resolve, { once: true });
          });
        }
        if (image.decode) await image.decode().catch(() => {});
      }
      if (!this.isConnected || requestId !== this.requestId) return;
      this.removeAttribute("data-initial-slide");
      this.slides.forEach((slide, slideIndex) => {
        const active = slideIndex === index;
        slide.toggleAttribute("data-active", active);
        slide.classList.toggle("tns-slide-active", active);
        slide.setAttribute("aria-hidden", String(!active));
        slide.inert = !active;
      });
      this.buttons.forEach((button, buttonIndex) => {
        const active = buttonIndex === index;
        button.classList.toggle("tns-nav-active", active);
        button.setAttribute("aria-current", String(active));
      });
      this.index = index;
      if (manual && this.status) this.status.textContent = this.status.dataset.slideLabel + " " + (index + 1) + " / " + this.slides.length;
      this.schedule();
    }
  }
  customElements.define("hero-slideshow", HeroSlideshow);
})();
